import { toast } from "sonner";
import { useEffect, useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Package,
  CheckCircle2,
  Clock,
  Truck,
  XCircle,
  Download,
  Eye,
  MapPin,
  StickyNote,
  Calendar,
  Receipt,
  CreditCard,
  Loader2,
  FileText,
  Hash,
  History,
  Search,
  SlidersHorizontal,
  X,
  MessageSquareText,
  Phone,
  Copy,
} from "lucide-react";
import { VoysCallButton } from "@/components/voys/VoysCallButton";
import { VoysCallHistory } from "@/components/voys/VoysCallHistory";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatEuro } from "@/lib/catalog";
import { downloadOrderPdf, generateOrderPdf, DEFAULT_PAYMENT_TERM_DAYS } from "@/lib/order-pdf";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { updateOrderPaymentTerm } from "@/lib/orders.functions";
import { Button } from "@/components/ui/button";
import { PageHeader } from "./profiel";

export const Route = createFileRoute("/_authenticated/account/bestellingen/$id")({
  head: () => ({
    meta: [
      { title: "Bestelling — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OrderDetailPage,
});

type LineItem = {
  slug: string;
  name: string;
  qty: number;
  list_price_cents?: number;
  unit_price_cents: number;
  line_total_cents: number;
};

type ShippingAddress = {
  full_name: string | null;
  company: string | null;
  street: string;
  house_number: string | null;
  postal_code: string;
  city: string;
  country: string | null;
};

type Order = {
  id: string;
  order_number: string;
  status: "pending" | "processing" | "completed" | "cancelled";
  total_cents: number;
  created_at: string;
  updated_at: string;
  line_items: LineItem[] | null;
  notes: string | null;
  payment_term_days: number | null;
  shipping_address: ShippingAddress | null;
};

const PAYMENT_TERM_OPTIONS = [7, 14, 30, 60, 90];

const statusConfig: Record<
  Order["status"],
  { label: string; badge: string; ring: string; icon: React.ComponentType<{ className?: string }>; desc: string }
> = {
  pending: {
    label: "In afwachting",
    badge: "bg-amber-50 text-amber-800 border-amber-200",
    ring: "from-amber-500 to-orange-400",
    icon: Clock,
    desc: "Uw bestelling is ontvangen en wacht op bevestiging.",
  },
  processing: {
    label: "In behandeling",
    badge: "bg-blue-50 text-blue-800 border-blue-200",
    ring: "from-blue-500 to-sky-400",
    icon: Truck,
    desc: "Uw bestelling wordt verwerkt en voorbereid voor verzending.",
  },
  completed: {
    label: "Afgerond",
    badge: "bg-emerald-50 text-emerald-800 border-emerald-200",
    ring: "from-emerald-500 to-teal-400",
    icon: CheckCircle2,
    desc: "Uw bestelling is succesvol afgerond.",
  },
  cancelled: {
    label: "Geannuleerd",
    badge: "bg-red-50 text-red-800 border-red-200",
    ring: "from-red-500 to-rose-400",
    icon: XCircle,
    desc: "Deze bestelling is geannuleerd.",
  },
};

const TIMELINE: Array<{ key: Order["status"]; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: "pending", label: "Ontvangen", icon: Receipt },
  { key: "processing", label: "In behandeling", icon: Package },
  { key: "completed", label: "Afgerond", icon: CheckCircle2 },
];

function OrderDetailPage() {
  const { id } = Route.useParams();
  const { user, profile } = useAuth();
  const saveTermFn = useServerFn(updateOrderPaymentTerm);
  const [savingTerm, setSavingTerm] = useState(false);

  // Status history filter state — persisted across sessions
  const HISTORY_FILTERS_KEY = "order-history-filters";
  type PersistedFilters = {
    historySearch: string;
    statusFilter: "all" | Order["status"];
    dateFilter: "all" | "today" | "week" | "month";
    showFilters: boolean;
  };
  const loadFilters = (): PersistedFilters | null => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(HISTORY_FILTERS_KEY);
      return raw ? (JSON.parse(raw) as PersistedFilters) : null;
    } catch {
      return null;
    }
  };
  const initialFilters = loadFilters();
  const [historySearch, setHistorySearch] = useState(initialFilters?.historySearch ?? "");
  const [statusFilter, setStatusFilter] = useState<"all" | Order["status"]>(
    initialFilters?.statusFilter ?? "all",
  );
  const [dateFilter, setDateFilter] = useState<"all" | "today" | "week" | "month">(
    initialFilters?.dateFilter ?? "all",
  );
  const [showFilters, setShowFilters] = useState(initialFilters?.showFilters ?? false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(
        HISTORY_FILTERS_KEY,
        JSON.stringify({ historySearch, statusFilter, dateFilter, showFilters }),
      );
    } catch {
      /* ignore quota errors */
    }
  }, [historySearch, statusFilter, dateFilter, showFilters]);

  const { data: order, isLoading, refetch } = useQuery({
    queryKey: ["order-detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(
          "id, order_number, status, total_cents, created_at, updated_at, line_items, notes, payment_term_days, shipping_address:addresses(street, house_number, postal_code, city, country), guest_phone, user_id",
        )
        .eq("id", id)
        .maybeSingle<Order & { guest_phone: string | null; user_id: string | null }>();
      if (error) throw error;
      if (!data) throw notFound();
      return data;
    },
  });

  const { data: orderProfile } = useQuery({
    queryKey: ["order-profile", order?.user_id],
    queryFn: async () => {
      if (!order?.user_id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("phone, full_name, email")
        .eq("id", order.user_id)
        .maybeSingle();
      if (error) throw error;
      return data as { phone: string | null; full_name: string | null; email: string | null } | null;
    },
    enabled: !!order?.user_id,
  });

  const { data: history } = useQuery({
    queryKey: ["order-status-history", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_status_history")
        // internal_note is staff-only en wordt bewust niet opgehaald
        .select("id, from_status, to_status, note, created_at")
        .eq("order_id", id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        from_status: Order["status"] | null;
        to_status: Order["status"];
        note: string | null;
        created_at: string;
      }>;
    },
  });

  // Filter history
  const filteredHistory = (history ?? []).filter((entry) => {
    const search = historySearch.toLowerCase();
    const matchesSearch =
      search === "" ||
      (entry.note?.toLowerCase().includes(search) ?? false) ||
      statusConfig[entry.to_status].label.toLowerCase().includes(search) ||
      (entry.from_status && statusConfig[entry.from_status].label.toLowerCase().includes(search));

    const matchesStatus = statusFilter === "all" || entry.to_status === statusFilter || entry.from_status === statusFilter;

    let matchesDate = true;
    if (dateFilter !== "all") {
      const entryDate = new Date(entry.created_at);
      const now = new Date();
      if (dateFilter === "today") {
        matchesDate =
          entryDate.getDate() === now.getDate() &&
          entryDate.getMonth() === now.getMonth() &&
          entryDate.getFullYear() === now.getFullYear();
      } else if (dateFilter === "week") {
        const weekAgo = new Date(now.getTime() - 7 * 86400000);
        matchesDate = entryDate >= weekAgo;
      } else if (dateFilter === "month") {
        const monthAgo = new Date(now.getTime() - 30 * 86400000);
        matchesDate = entryDate >= monthAgo;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-40 animate-pulse rounded-lg bg-muted" />
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
        <div className="h-64 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Package} title="Bestelling" subtitle="Bestelling niet gevonden." />
        <Link to="/account/bestellingen" className="text-sm font-semibold text-[#3a7bd5] hover:underline">
          ← Terug naar bestellingen
        </Link>
      </div>
    );
  }

  const cfg = statusConfig[order.status];
  const StatusIcon = cfg.icon;
  const termDays = order.payment_term_days ?? DEFAULT_PAYMENT_TERM_DAYS;
  const createdDate = new Date(order.created_at);
  const dueDate = new Date(createdDate.getTime() + termDays * 86400000);
  const cancelled = order.status === "cancelled";
  const currentStep =
    order.status === "completed" ? 3 : order.status === "processing" ? 2 : order.status === "pending" ? 1 : 0;
  const subtotal = (order.line_items ?? []).reduce((s, l) => s + l.line_total_cents, 0);
  const orderPhone = order.guest_phone || orderProfile?.phone || null;

  const orderForPdf = () => ({
    order_number: order.order_number,
    status: order.status,
    total_cents: order.total_cents,
    created_at: order.created_at,
    line_items: order.line_items ?? [],
    notes: order.notes,
    shipping_address: order.shipping_address,
    customer: {
      full_name: profile?.full_name ?? null,
      email: user?.email ?? null,
    },
  });

  const handleDownload = () => {
    downloadOrderPdf(orderForPdf(), termDays);
  };

  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSetTerm = async (days: number) => {
    setSavingTerm(true);
    try {
      await saveTermFn({ data: { orderId: order.id, paymentTermDays: days } });
      await refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Opslaan mislukt");
    } finally {
      setSavingTerm(false);
    }
  };

  return (
    <div className="space-y-8">
      <Link
        to="/account/bestellingen"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#3a7bd5] hover:underline"
      >
        <ArrowLeft className="size-4" /> Terug naar overzicht
      </Link>

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0b2a4a] via-[#0e3463] to-[#1a4a85] p-6 text-white shadow-xl sm:p-8">
        <div className={`absolute -right-16 -top-16 size-56 rounded-full bg-gradient-to-br ${cfg.ring} opacity-30 blur-3xl`} />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider ring-1 ring-white/20 backdrop-blur">
              <Hash className="size-3" />
              {order.order_number}
            </div>
            <h1 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Bestelling van{" "}
              {createdDate.toLocaleDateString("nl-NL", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </h1>
            <p className="mt-2 max-w-xl text-sm text-white/70">{cfg.desc}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${cfg.badge}`}
              >
                <StatusIcon className="size-3.5" />
                {cfg.label}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium ring-1 ring-white/20">
                {order.line_items?.length ?? 0} product{order.line_items?.length === 1 ? "" : "en"}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs font-medium ring-1 ring-white/20">
                Totaal {formatEuro(order.total_cents)}
              </span>
            </div>
            {orderPhone && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a
                  href={`tel:${orderPhone}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-white/90 hover:text-white hover:underline"
                >
                  <Phone className="size-3.5" />
                  {orderPhone}
                </a>
                <VoysCallButton phone={orderPhone} size="xs" showLabel={false} variant="secondary" />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(orderPhone);
                    toast.success("Telefoonnummer gekopieerd");
                  }}
                  className="inline-flex items-center justify-center rounded-md bg-white/10 p-1.5 text-white/80 ring-1 ring-white/20 transition hover:bg-white/20 hover:text-white"
                  title="Telefoonnummer kopiëren"
                >
                  <Copy className="size-3" />
                </button>
                <a
                  href={`https://wa.me/${orderPhone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#25D366] px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#1ebe57]"
                  title="Start WhatsApp-gesprek"
                >
                  <MessageSquareText className="size-3.5" />
                  WhatsApp
                </a>
                <VoysCallHistory phone={orderPhone} limit={3} className="text-white/70" />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
            >
              <Eye className="size-4" />
              Voorbeeld
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#0b2a4a] shadow-lg transition hover:bg-white/90"
            >
              <Download className="size-4" />
              Factuur (PDF)
            </button>
            <a
              href={`/api/public/invoices/pdf?id=${order.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
            >
              <Download className="size-4" />
              Bundel (PDF)
            </a>
            <a
              href={`/api/public/invoices/zip?id=${order.id}`}
              className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
            >
              <Download className="size-4" />
              Alles (ZIP)
            </a>

          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
        <h2 className="font-display text-lg font-bold">Planning & status</h2>
        <p className="mt-1 text-xs text-text-secondary">
          Volg de voortgang van uw bestelling
        </p>

        {cancelled ? (
          <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            <XCircle className="size-5 shrink-0" /> Deze bestelling is geannuleerd.
          </div>
        ) : (
          <ol className="mt-6 grid gap-4 sm:grid-cols-3">
            {TIMELINE.map((step, idx) => {
              const stepNum = idx + 1;
              const done = stepNum < currentStep || order.status === "completed";
              const active = stepNum === currentStep && order.status !== "completed";
              const Icon = step.icon;
              return (
                <li
                  key={step.key}
                  className={`relative rounded-xl border p-4 ${
                    done
                      ? "border-emerald-200 bg-emerald-50/50"
                      : active
                        ? "border-[#3a7bd5]/40 bg-[#3a7bd5]/5"
                        : "border-border bg-background"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex size-9 items-center justify-center rounded-full ${
                        done
                          ? "bg-emerald-500 text-white"
                          : active
                            ? "bg-gradient-to-br from-[#3a7bd5] to-[#7dd3fc] text-white"
                            : "bg-muted text-text-secondary"
                      }`}
                    >
                      {done ? <CheckCircle2 className="size-4" /> : <Icon className="size-4" />}
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">
                        Stap {stepNum}
                      </p>
                      <p className="text-sm font-semibold">{step.label}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="mt-6 grid gap-4 border-t border-border pt-5 sm:grid-cols-2">
          <DateRow icon={Calendar} label="Geplaatst op" value={createdDate.toLocaleString("nl-NL")} />
          <DateRow
            icon={Clock}
            label="Laatste update"
            value={new Date(order.updated_at).toLocaleString("nl-NL")}
          />
          {!cancelled && (
            <DateRow
              icon={CreditCard}
              label={`Te voldoen binnen ${termDays} dagen`}
              value={`Vervaldatum: ${dueDate.toLocaleDateString("nl-NL", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}`}
            />
          )}
        </div>
      </section>

      {/* Line items */}
      <section className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="border-b border-border px-6 py-4">
          <h2 className="font-display text-lg font-bold">Producten</h2>
        </div>
        <ul className="divide-y divide-border">
          {(order.line_items ?? []).map((item, idx) => (
            <li key={`${item.slug}-${idx}`} className="flex items-center gap-4 px-6 py-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-text-secondary">
                <Package className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{item.name}</p>
                <p className="mt-0.5 text-xs text-text-secondary">
                  {item.qty} × {formatEuro(item.unit_price_cents)}
                </p>
              </div>
              <p className="text-sm font-bold">{formatEuro(item.line_total_cents)}</p>
            </li>
          ))}
        </ul>
        <div className="space-y-1.5 border-t border-border bg-muted/30 px-6 py-4 text-sm">
          <div className="flex justify-between text-text-secondary">
            <span>Subtotaal</span>
            <span>{formatEuro(subtotal)}</span>
          </div>
          <div className="flex justify-between text-base font-bold">
            <span>Totaal</span>
            <span>{formatEuro(order.total_cents)}</span>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Address */}
        <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <MapPin className="size-4 text-[#3a7bd5]" />
            <h2 className="font-display text-lg font-bold">Bezorgadres</h2>
          </div>
          {order.shipping_address ? (
            <div className="mt-3 space-y-0.5 text-sm">
              {order.shipping_address.full_name && (
                <p className="font-semibold">{order.shipping_address.full_name}</p>
              )}
              {order.shipping_address.company && (
                <p className="text-text-secondary">{order.shipping_address.company}</p>
              )}
              <p>
                {order.shipping_address.street} {order.shipping_address.house_number}
              </p>
              <p>
                {order.shipping_address.postal_code} {order.shipping_address.city}
              </p>
              {order.shipping_address.country && <p>{order.shipping_address.country}</p>}
            </div>
          ) : (
            <p className="mt-3 text-sm text-text-secondary">Geen bezorgadres geregistreerd.</p>
          )}
        </section>

        {/* Payment term */}
        <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <CreditCard className="size-4 text-[#3a7bd5]" />
            <h2 className="font-display text-lg font-bold">Betaaltermijn</h2>
          </div>
          <p className="mt-1 text-xs text-text-secondary">
            Kies een termijn voor deze bestelling. De factuur wordt aangepast aan uw keuze.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {PAYMENT_TERM_OPTIONS.map((d) => {
              const active = termDays === d;
              return (
                <button
                  key={d}
                  disabled={savingTerm || cancelled}
                  onClick={() => handleSetTerm(d)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                    active
                      ? "border-[#3a7bd5] bg-[#3a7bd5] text-white"
                      : "border-border bg-background hover:border-[#3a7bd5]/40 hover:bg-[#3a7bd5]/5"
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {d} dagen
                </button>
              );
            })}
            {savingTerm && <Loader2 className="size-4 animate-spin self-center text-text-secondary" />}
          </div>
        </section>
      </div>

      {/* Status history */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
        <div className="flex items-center gap-2">
          <History className="size-4 text-[#3a7bd5]" />
          <h2 className="font-display text-lg font-bold">Statusgeschiedenis</h2>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          Alle wijzigingen aan deze bestelling, met datum en tijd
        </p>

        {/* Search & Filters */}
        <div className="mt-4 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Zoek op status, notitie of opmerking..."
                className="w-full rounded-xl border border-border bg-background py-2.5 pl-9 pr-8 text-sm outline-none transition focus:border-[#3a7bd5] focus:ring-1 focus:ring-[#3a7bd5]"
              />
              {historySearch && (
                <button
                  onClick={() => setHistorySearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-text-secondary hover:bg-muted hover:text-foreground"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                showFilters || statusFilter !== "all" || dateFilter !== "all"
                  ? "border-[#3a7bd5] bg-[#3a7bd5]/10 text-[#3a7bd5]"
                  : "border-border bg-background text-foreground hover:border-[#3a7bd5]/40"
              }`}
            >
              <SlidersHorizontal className="size-4" />
              Filters
              {(statusFilter !== "all" || dateFilter !== "all") && (
                <span className="flex size-5 items-center justify-center rounded-full bg-[#3a7bd5] text-[10px] font-bold text-white">
                  {(statusFilter !== "all" ? 1 : 0) + (dateFilter !== "all" ? 1 : 0)}
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-3 rounded-xl border border-border bg-muted/30 p-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Status</span>
                <div className="flex flex-wrap gap-1.5">
                  {(["all", "pending", "processing", "completed", "cancelled"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={`rounded-lg border px-2.5 py-1 text-xs font-semibold transition ${
                        statusFilter === s
                          ? "border-[#3a7bd5] bg-[#3a7bd5] text-white"
                          : "border-border bg-background hover:border-[#3a7bd5]/40"
                      }`}
                    >
                      {s === "all" ? "Alles" : statusConfig[s].label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Periode</span>
                <div className="flex flex-wrap gap-1.5">
                  {([
                    { key: "all" as const, label: "Alles" },
                    { key: "today" as const, label: "Vandaag" },
                    { key: "week" as const, label: "Laatste 7 dagen" },
                    { key: "month" as const, label: "Laatste 30 dagen" },
                  ]).map((d) => (
                    <button
                      key={d.key}
                      onClick={() => setDateFilter(d.key)}
                      className={`rounded-lg border px-2.5 py-1 text-xs font-semibold transition ${
                        dateFilter === d.key
                          ? "border-[#3a7bd5] bg-[#3a7bd5] text-white"
                          : "border-border bg-background hover:border-[#3a7bd5]/40"
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              {(statusFilter !== "all" || dateFilter !== "all") && (
                <button
                  onClick={() => {
                    setStatusFilter("all");
                    setDateFilter("all");
                  }}
                  className="self-end rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-text-secondary transition hover:border-red-300 hover:text-red-600"
                >
                  Wis filters
                </button>
              )}
            </div>
          )}
        </div>

        {!history || history.length === 0 ? (
          <p className="mt-4 text-sm text-text-secondary">
            Nog geen updates geregistreerd.
          </p>
        ) : filteredHistory.length === 0 ? (
          <div className="mt-5 rounded-xl border border-border bg-muted/20 p-6 text-center">
            <p className="text-sm font-semibold text-foreground">Geen resultaten gevonden</p>
            <p className="mt-1 text-xs text-text-secondary">
              Pas uw zoekterm of filters aan om updates te vinden.
            </p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-xs text-text-secondary">
              {filteredHistory.length} van {history.length} update{filteredHistory.length === 1 ? "" : "s"} getoond
            </p>
            <ol className="mt-3 relative border-l border-border pl-6">
              {filteredHistory.map((entry) => {
                const to = statusConfig[entry.to_status];
                const from = entry.from_status ? statusConfig[entry.from_status] : null;
                const ToIcon = to.icon;
                const ts = new Date(entry.created_at);
                return (
                <li key={entry.id} className="relative mb-5 last:mb-0">
                  <span
                    className={`absolute -left-[33px] flex size-6 items-center justify-center rounded-full ring-4 ring-card bg-gradient-to-br ${to.ring} text-white`}
                  >
                    <ToIcon className="size-3" />
                  </span>
                  <div className="rounded-xl border border-border bg-background p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {from ? (
                        <p className="text-sm font-semibold">
                          <span className="text-text-secondary">{from.label}</span>{" "}
                          <span className="text-text-secondary">→</span> {to.label}
                        </p>
                      ) : (
                        <p className="text-sm font-semibold">{to.label}</p>
                      )}
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${to.badge}`}
                      >
                        {to.label}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-text-secondary">
                      {ts.toLocaleString("nl-NL", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                    {entry.note && (
                      <p className="mt-2 text-sm">{entry.note}</p>
                    )}
                  </div>
                </li>
              );
              })}
            </ol>
          </>
        )}
      </section>

      {/* Documents */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <FileText className="size-4 text-[#3a7bd5]" />
          <h2 className="font-display text-lg font-bold">Documenten</h2>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          Download de officiële documenten van deze bestelling
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <DocumentCard
            title="Factuur / Pakbon"
            subtitle={`Vervalt ${dueDate.toLocaleDateString("nl-NL")}`}
            onClick={handleDownload}
          />
          <DocumentCard
            title="Orderbevestiging"
            subtitle="Volledige bestelspecificatie"
            onClick={handleDownload}
          />
        </div>
      </section>

      {/* Notes */}
      {order.notes && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
          <div className="flex items-center gap-2 text-amber-900">
            <StickyNote className="size-4" />
            <h2 className="font-display text-lg font-bold">Opmerkingen</h2>
          </div>
          <p className="mt-2 text-sm text-amber-900">{order.notes}</p>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <Button asChild variant="outline">
          <Link to="/account/bestellingen">Terug naar overzicht</Link>
        </Button>
        <Button asChild>
          <Link to="/klantenservice">Vraag over deze bestelling</Link>
        </Button>
      </div>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={`Factuur ${order.order_number}`}
        description="Voorvertoning — controleer voordat je downloadt"
        fileName={`Factuur-${order.order_number}.pdf`}
        source={{
          kind: "blob",
          getBlobUrl: async () => {
            const doc = await generateOrderPdf(orderForPdf(), termDays);
            return doc.output("bloburl") as unknown as string;
          },
        }}
      />
    </div>
  );
}

function DateRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-background p-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-[#3a7bd5]">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary">
          {label}
        </p>
        <p className="mt-0.5 text-sm font-semibold">{value}</p>
      </div>
    </div>
  );
}

function DocumentCard({
  title,
  subtitle,
  onClick,
}: {
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-4 rounded-xl border border-border bg-background p-4 text-left transition hover:border-[#3a7bd5]/40 hover:bg-[#3a7bd5]/5"
    >
      <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#0b2a4a] to-[#3a7bd5] text-white shadow-md">
        <FileText className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{title}</p>
        <p className="truncate text-xs text-text-secondary">{subtitle}</p>
      </div>
      <Download className="size-4 text-text-secondary transition group-hover:text-[#3a7bd5]" />
    </button>
  );
}
